import React from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  changedCell: {
    backgroundColor: '#fff3cd',
    fontWeight: 'bold',
  },
  unchangedCell: {
    backgroundColor: '#f5f5f5',
  },
}))

const CompareUpdate = ({ open, onClose, originalCourse, pendingCourse }) => {
  const classes = useStyles()

  if (!originalCourse || !pendingCourse) {
    return null
  }

  const fieldsToCompare = [
    'name',
    'address',
    'city',
    'state',
    'zip',
    'country',
    'phone',
    'website',
  ]

  const getRowStyle = (field) => {
    return originalCourse[field] !== pendingCourse[field] ? classes.changedCell : classes.unchangedCell
  }

  const compareScorecards = () => {
    const originalScorecard = originalCourse.scorecard || []
    const pendingScorecard = pendingCourse.scorecard || []
    
    const maxHoles = Math.max(originalScorecard.length, pendingScorecard.length)
    const differences = []
    
    for (let i = 0; i < maxHoles; i++) {
      const originalHole = originalScorecard[i] || {}
      const pendingHole = pendingScorecard[i] || {}
      
      if (JSON.stringify(originalHole) !== JSON.stringify(pendingHole)) {
        differences.push({
          hole: i + 1,
          original: originalHole,
          pending: pendingHole,
        })
      }
    }
    
    return differences
  }

  const formatHoleData = (hole) => {
    if (!hole || !hole.tees) return {}
    const tees = hole.tees || {}
    const formatted = {}
    
    Object.keys(tees).forEach((teeKey) => {
      const tee = tees[teeKey]
      if (tee) {
        formatted[teeKey] = {
          color: tee.color,
          yards: tee.yards,
        }
      }
    })
    
    return formatted
  }

  const getTeeValue = (tee, field) => {
    return tee && tee[field] ? tee[field] : '-'
  }

  const teeValueChanged = (origTee, pendTee, field) => {
    const origVal = origTee ? origTee[field] : undefined
    const pendVal = pendTee ? pendTee[field] : undefined
    return origVal !== pendVal
  }

  const scorecardDifferences = compareScorecards()

  const compareTeeBoxes = () => {
    const originalTeeBoxes = originalCourse.teeBoxes || []
    const pendingTeeBoxes = pendingCourse.teeBoxes || []
    
    const maxTeeBoxes = Math.max(originalTeeBoxes.length, pendingTeeBoxes.length)
    const differences = []
    
    for (let i = 0; i < maxTeeBoxes; i++) {
      const originalTee = originalTeeBoxes[i] || {}
      const pendingTee = pendingTeeBoxes[i] || {}
      
      if (JSON.stringify(originalTee) !== JSON.stringify(pendingTee)) {
        differences.push({
          index: i,
          original: originalTee,
          pending: pendingTee,
        })
      }
    }
    
    return differences
  }

  const teeBoxDifferences = compareTeeBoxes()

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Compare Course Update</DialogTitle>
      <DialogContent>
        <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '16px' }}>
          Course Details
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow style={{ backgroundColor: '#e8e8e8' }}>
                <TableCell style={{ fontWeight: 'bold' }}>Field</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Current</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Proposed Change</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fieldsToCompare.map((field) => (
                <TableRow key={field} className={getRowStyle(field)}>
                  <TableCell style={{ fontWeight: 'bold' }}>
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </TableCell>
                  <TableCell>{originalCourse[field] || '-'}</TableCell>
                  <TableCell>{pendingCourse[field] || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {scorecardDifferences.length > 0 && (
          <>
            <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '24px' }}>
              Scorecard Changes
            </Typography>
            {scorecardDifferences.map((diff) => {
              const origTees = formatHoleData(diff.original)
              const pendTees = formatHoleData(diff.pending)
              const allTeeKeys = new Set([
                ...Object.keys(origTees),
                ...Object.keys(pendTees),
              ])
              const parChanged = diff.original.Par !== diff.pending.Par
              const handicapChanged = diff.original.Handicap !== diff.pending.Handicap

              return (
                <TableContainer component={Paper} style={{ marginBottom: '16px' }} key={diff.hole}>
                  <Typography style={{ padding: '12px', fontWeight: 'bold', backgroundColor: '#fff3cd' }}>
                    Hole {diff.hole}
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell style={{ fontWeight: 'bold' }}>Tee Box</TableCell>
                        <TableCell align="center" style={{ fontWeight: 'bold' }}>Color</TableCell>
                        <TableCell align="center" style={{ fontWeight: 'bold' }}>Yards</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from(allTeeKeys).map((teeKey) => {
                        const origTee = origTees[teeKey]
                        const pendTee = pendTees[teeKey]
                        const colorChanged = teeValueChanged(origTee, pendTee, 'color')
                        const yardsChanged = teeValueChanged(origTee, pendTee, 'yards')
                        const teeChanged = colorChanged || yardsChanged

                        return (
                          <TableRow key={teeKey} className={teeChanged ? classes.changedCell : ''}>
                            <TableCell style={{ fontWeight: 'bold' }}>{teeKey}</TableCell>
                            <TableCell align="center" style={colorChanged ? { fontWeight: 'bold' } : {}}>
                              {getTeeValue(origTee, 'color')} {colorChanged && `→ ${getTeeValue(pendTee, 'color')}`}
                            </TableCell>
                            <TableCell align="center" style={yardsChanged ? { fontWeight: 'bold' } : {}}>
                              {getTeeValue(origTee, 'yards')} {yardsChanged && `→ ${getTeeValue(pendTee, 'yards')}`}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      <TableRow className={parChanged ? classes.changedCell : ''}>
                        <TableCell style={{ fontWeight: 'bold' }}>Par</TableCell>
                        <TableCell colSpan={2} align="center" style={parChanged ? { fontWeight: 'bold' } : {}}>
                          {diff.original.Par} {parChanged && `→ ${diff.pending.Par}`}
                        </TableCell>
                      </TableRow>
                      <TableRow className={handicapChanged ? classes.changedCell : ''}>
                        <TableCell style={{ fontWeight: 'bold' }}>Handicap</TableCell>
                        <TableCell colSpan={2} align="center" style={handicapChanged ? { fontWeight: 'bold' } : {}}>
                          {diff.original.Handicap} {handicapChanged && `→ ${diff.pending.Handicap}`}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            })}
          </>
        )}

        {teeBoxDifferences.length > 0 && (
          <>
            <Typography variant="h6" style={{ marginBottom: '16px', marginTop: '24px' }}>
              Tee Box Changes
            </Typography>
            {teeBoxDifferences.map((diff) => {
              const origTee = diff.original
              const pendTee = diff.pending
              const teeNameChanged = origTee.tee !== pendTee.tee
              const slopeChanged = origTee.slope !== pendTee.slope
              const handicapChanged = origTee.handicap !== pendTee.handicap

              return (
                <TableContainer component={Paper} style={{ marginBottom: '16px' }} key={diff.index}>
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell style={{ fontWeight: 'bold' }}>Tee Box {diff.index + 1}</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>Current</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>Proposed Change</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow className={teeNameChanged ? classes.changedCell : ''}>
                        <TableCell style={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell>{origTee.tee || '-'}</TableCell>
                        <TableCell>{pendTee.tee || '-'}</TableCell>
                      </TableRow>
                      <TableRow className={slopeChanged ? classes.changedCell : ''}>
                        <TableCell style={{ fontWeight: 'bold' }}>Slope</TableCell>
                        <TableCell>{origTee.slope || '-'}</TableCell>
                        <TableCell>{pendTee.slope || '-'}</TableCell>
                      </TableRow>
                      <TableRow className={handicapChanged ? classes.changedCell : ''}>
                        <TableCell style={{ fontWeight: 'bold' }}>Handicap</TableCell>
                        <TableCell>{origTee.handicap || '-'}</TableCell>
                        <TableCell>{pendTee.handicap || '-'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompareUpdate
